import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { Bell, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SEVERITY } from "@/lib/alerts";
import { ALERTS } from "@/constants/testIds";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

function rel(ts) {
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ""; }
}

export function NotificationBell() {
  const { active } = useWorkspace();
  const navigate = useNavigate();
  const [breaches, setBreaches] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get("/alerts/breaches", { params: { workspace_id: active.id } });
      setBreaches(data);
    } catch {
      /* ignore */
    }
  }, [active]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const count = breaches.length;
  const top = breaches[0]?.severity || "low";

  const clearAll = async () => {
    setBreaches([]);
    try {
      await api.post("/alerts/breaches/clear", { workspace_id: active.id });
    } catch { /* ignore */ }
  };

  const dismissOne = async (runId) => {
    setBreaches((prev) => prev.filter((b) => b.run_id !== runId));
    try {
      await api.post("/alerts/breaches/clear", { workspace_id: active.id, run_id: runId });
    } catch { /* ignore */ }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={ALERTS.bell} className="relative rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground" aria-label="Alerts">
          <Bell className="h-4 w-4" />
          {count > 0 && (
            <span data-testid={ALERTS.bellBadge}
              className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold text-background ${SEVERITY[top]?.dot || "bg-accent"}`}>
              {count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 font-mono">
        <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
          <span className="text-sm font-semibold">Recent breaches</span>
          <div className="flex items-center gap-3">
            {count > 0 && (
              <button onClick={clearAll} data-testid={ALERTS.bellClearAll} className="text-[11px] text-muted-foreground hover:text-foreground hover:underline">
                Clear all
              </button>
            )}
            <button onClick={() => { setOpen(false); navigate("/alerts"); }} className="text-[11px] text-accent hover:underline">
              View all
            </button>
          </div>
        </div>
        <ScrollArea className="max-h-80">
          {count === 0 ? (
            <div className="px-4 py-10 text-center text-xs text-muted-foreground">No breaches. All metrics nominal.</div>
          ) : (
            breaches.map((b) => {
              const sev = SEVERITY[b.severity] || SEVERITY.low;
              return (
                <div key={b.run_id} className="group flex items-start border-b border-border/30 last:border-0 hover:bg-muted/30">
                  <button data-testid={ALERTS.bellItem}
                    onClick={() => { setOpen(false); navigate(`/alerts/${b.alert_id}`); }}
                    className="flex min-w-0 flex-1 items-start gap-2.5 px-4 py-3 text-left">
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${sev.dot}`} />
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">{b.alert_name}</p>
                      <p className="truncate text-[11px] text-muted-foreground">{b.detail}</p>
                      <p className="text-[10px] text-muted-foreground/70">{rel(b.ran_at)}</p>
                    </div>
                  </button>
                  <button onClick={() => dismissOne(b.run_id)} data-testid={ALERTS.bellDismissItem} aria-label="Dismiss notification"
                    className="mr-2 mt-3 rounded-full p-1 text-muted-foreground/60 opacity-0 transition-opacity hover:bg-muted/50 hover:text-foreground group-hover:opacity-100">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
